/* tslint:disable: jsx-no-lambda */
import * as React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Tooltip,
} from '@material-ui/core';
import AsyncSelect from 'react-select/async';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';

import {
  documentType,
  genderOptions,
  emptyRow,
  loadCityOptions,
  cities,
  MAX_ROWS,
} from 'pages/BulkQuotation/resources';
import { useStyles } from './styles';

export type TRowErrors = Partial<Record<keyof IBulkQuoteRow, string>>[];

interface RowsTableProps {
  rows: IBulkQuoteRow[];
  onChange: (rows: IBulkQuoteRow[]) => void;
  rowErrors?: TRowErrors;
}

const citySelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 40,
    borderColor: state.selectProps['aria-invalid'] ? '#f44336' : '#c4c4c4',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const RowsTable: React.FC<RowsTableProps> = ({ rows, onChange, rowErrors = [] }) => {
  const classes = useStyles();

  const update = (i: number, field: keyof IBulkQuoteRow, value: string) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [field]: value } : r));
    onChange(next);
  };

  const updateCity = (i: number, value: IBulkQuoteOption | null) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, city: value } : r));
    onChange(next);
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    onChange([...rows, emptyRow()]);
  };

  const removeRow = (i: number) => {
    if (rows.length === 1) {
      onChange([emptyRow()]);
      return;
    }
    onChange(rows.filter((_, idx) => idx !== i));
  };

  const cellError = (i: number, field: keyof IBulkQuoteRow) =>
    (rowErrors[i] && rowErrors[i][field]) || '';

  const inputProps = {
    variant: 'outlined' as const,
    size: 'small' as const,
    fullWidth: true,
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.scroll}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow className={classes.headRow}>
              <TableCell className={classes.idx}>#</TableCell>
              <TableCell className={classes.name}>Nombres</TableCell>
              <TableCell className={classes.name}>Apellidos</TableCell>
              <TableCell className={classes.narrow}>Tipo doc.</TableCell>
              <TableCell className={classes.doc}>N° documento</TableCell>
              <TableCell className={classes.narrow}>Género</TableCell>
              <TableCell className={classes.mid}>Nacimiento</TableCell>
              <TableCell className={classes.mid}>Placa</TableCell>
              <TableCell className={classes.city}>Ciudad de circulación</TableCell>
              <TableCell className={classes.idx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i} className={classes.bodyRow}>
                <TableCell className={classes.idx}>{i + 1}</TableCell>
                <TableCell>
                  <TextField
                    {...inputProps}
                    value={row.firstName}
                    error={!!cellError(i, 'firstName')}
                    helperText={cellError(i, 'firstName')}
                    onChange={(e) => update(i, 'firstName', e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    {...inputProps}
                    value={row.lastName}
                    error={!!cellError(i, 'lastName')}
                    helperText={cellError(i, 'lastName')}
                    onChange={(e) => update(i, 'lastName', e.target.value)}
                  />
                </TableCell>
                <TableCell className={classes.narrow}>
                  <TextField
                    {...inputProps}
                    select
                    value={row.documentType}
                    onChange={(e) => update(i, 'documentType', e.target.value)}>
                    {documentType.map((d: { value: string; label: string }) => (
                      <MenuItem key={d.value} value={d.value}>
                        {d.value.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    {...inputProps}
                    value={row.nDocument}
                    error={!!cellError(i, 'nDocument')}
                    helperText={cellError(i, 'nDocument')}
                    onChange={(e) => update(i, 'nDocument', e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </TableCell>
                <TableCell className={classes.narrow}>
                  <TextField
                    {...inputProps}
                    select
                    value={row.gender}
                    onChange={(e) => update(i, 'gender', e.target.value)}>
                    {genderOptions.map((g) => (
                      <MenuItem key={g.value} value={g.value}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell className={classes.mid}>
                  <NumberFormat
                    {...(inputProps as any)}
                    customInput={TextField}
                    format="##/##/####"
                    placeholder="DD/MM/AAAA"
                    value={row.fBorn}
                    onValueChange={(v: NumberFormatValues) => update(i, 'fBorn', v.formattedValue)}
                    error={!!cellError(i, 'fBorn')}
                    helperText={cellError(i, 'fBorn')}
                  />
                </TableCell>
                <TableCell className={classes.mid}>
                  <TextField
                    {...inputProps}
                    value={row.licenseVehicle}
                    error={!!cellError(i, 'licenseVehicle')}
                    helperText={cellError(i, 'licenseVehicle')}
                    onChange={(e) =>
                      update(i, 'licenseVehicle', e.target.value.toUpperCase().trim())
                    }
                  />
                </TableCell>
                <TableCell className={classes.city}>
                  <AsyncSelect
                    placeholder="Buscar ciudad..."
                    cacheOptions
                    defaultOptions={cities.slice(0, 50)}
                    loadOptions={loadCityOptions}
                    value={row.city}
                    onChange={(v: any) => updateCity(i, (v as IBulkQuoteOption) || null)}
                    styles={citySelectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    menuPlacement="auto"
                    aria-invalid={!!cellError(i, 'city')}
                  />
                  {cellError(i, 'city') && (
                    <span className={classes.cityError}>{cellError(i, 'city')}</span>
                  )}
                </TableCell>
                <TableCell className={classes.idx}>
                  <Tooltip title={rows.length === 1 ? 'Al menos una fila' : 'Quitar fila'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={rows.length === 1}
                        onClick={() => removeRow(i)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={rows.length >= MAX_ROWS}
        className={classes.addBtn}>
        Agregar fila
      </Button>
    </div>
  );
};

export default RowsTable;
